import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare, CheckCircle2, Copy } from 'lucide-react';
import WebsiteKnowledgeCard from '@/components/WebsiteKnowledgeCard';
import api from '@/lib/api';
import { toast } from 'sonner';

function SourceBadge({ source }) {
  if (!source) return null;
  if (source === 'website') {
    return <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Auto dal sito</span>;
  }
  if (source === 'manual') {
    return <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Manuale</span>;
  }
  return null;
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [accessTokenConfigured, setAccessTokenConfigured] = useState(false);
  const [editingAccessToken, setEditingAccessToken] = useState(false);

  const [whatsappConfig, setWhatsappConfig] = useState({
    phone_number_id: '',
    access_token: '',
    verify_token: '',
    phone_number: '',
  });

  const [settings, setSettings] = useState({
    default_language: 'it',
    force_language_enabled: false,
    force_language_value: 'it',
    studio_public_name: 'Studio',
    public_info_url: '',
    girls_list: [],
    pricing_text: '',
    durations: '30,60,90',
    rules_text: '',
    location_text: '',
    welcome_text: '',
    external_details_link: '',
  });
  
  const [girlsText, setGirlsText] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [knowledgeData, setKnowledgeData] = useState(null);
  const [analyzeStatus, setAnalyzeStatus] = useState(null);
  const [fieldSources, setFieldSources] = useState({}); // null | 'success' | 'error'

  const fetchData = useCallback(async () => {
    try {
      const [whatsappRes, whatsappSetupRes, settingsRes] = await Promise.all([
        api.get('/whatsapp/config'),
        api.get('/whatsapp/setup-info'),
        api.get('/tenant/settings'),
      ]);

      const tokenIsConfigured = Boolean(whatsappRes.data.access_token_configured);
      setAccessTokenConfigured(tokenIsConfigured);
      setEditingAccessToken(false);
      setWhatsappConfig(prev => ({
        ...prev,
        phone_number_id: whatsappRes.data.phone_number_id || '',
        access_token: '',
        verify_token: whatsappSetupRes.data.verify_token || '',
        phone_number: whatsappRes.data.phone_number || '',
      }));

      if (settingsRes.data) {
        setSettings(prev => ({
          ...prev,
          ...settingsRes.data,
          durations: Array.isArray(settingsRes.data.durations)
            ? settingsRes.data.durations.join(',')
            : '30,60,90',
        }));
        
        // Initialize girlsText from girls_list
        if (Array.isArray(settingsRes.data.girls_list)) {
          setGirlsText(settingsRes.data.girls_list.join('\n'));
        }
        // Initialize website_url
        if (settingsRes.data.website_url) {
          setWebsiteUrl(settingsRes.data.website_url);
        }
        // Initialize field sources
        if (settingsRes.data._field_sources) {
          setFieldSources(settingsRes.data._field_sources);
        }
      }

      // Fetch existing knowledge
      try {
        const knowledgeRes = await api.get('/tenant/knowledge');
        if (knowledgeRes.data && knowledgeRes.data.source_url) {
          setKnowledgeData(knowledgeRes.data);
          setAnalyzeStatus('success');
        }
      } catch {}
    } catch (error) {
      toast.error(t('settings.loadError'));
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl) {
      toast.error('URL del sito web richiesto');
      return;
    }
    setAnalyzing(true);
    setAnalyzeStatus(null);
    try {
      const res = await api.post('/tenant/website/analyze', { website_url: websiteUrl });
      if (res.data.success) {
        setKnowledgeData(res.data);
        setAnalyzeStatus('success');
        var msg = res.data.pages_crawled + ' pagine analizzate';
        var af = res.data.autofilled_fields || [];
        if (af.length > 0) {
          msg += ', ' + af.length + ' campi auto-compilati';
        }
        toast.success(msg);
        // Refresh knowledge
        const knowledgeRes = await api.get('/tenant/knowledge');
        if (knowledgeRes.data) {
          setKnowledgeData(knowledgeRes.data);
        }
        // Reload settings to pick up auto-populated fields
        const settingsRes = await api.get('/tenant/settings');
        if (settingsRes.data) {
          setSettings(function(prev) {
            return {
              ...prev,
              ...settingsRes.data,
              durations: Array.isArray(settingsRes.data.durations)
                ? settingsRes.data.durations.join(',')
                : prev.durations || '30,60,90',
            };
          });
          if (Array.isArray(settingsRes.data.girls_list)) {
            setGirlsText(settingsRes.data.girls_list.join('\n'));
          }
          if (settingsRes.data._field_sources) {
            setFieldSources(settingsRes.data._field_sources);
          }
        }
      } else {
        setAnalyzeStatus('error');
        toast.error(res.data.error || 'Analisi fallita');
      }
    } catch (error) {
      setAnalyzeStatus('error');
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleWhatsAppSave = async () => {
    setLoading(true);
    try {
      await api.put('/whatsapp/config', whatsappConfig);
      toast.success(t('settings.whatsappSaved'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsSave = async () => {
    setLoading(true);
    try {
      // Parse girls_list from raw text (ONLY on save, not on keystroke)
      const girls_list = girlsText
        .split(/\r?\n|,/)
        .map(g => g.trim())
        .filter(g => g.length > 0);
      
      const payload = {
        ...settings,
        girls_list: girls_list,
        durations: [...new Set(
          settings.durations.split(',')
            .map(d => parseInt(d.trim()))
            .filter(d => !isNaN(d) && d > 0)
        )].sort((a, b) => a - b),
      };
      await api.put('/tenant/settings', payload);
      toast.success(t('settings.settingsSaved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhook/whatsapp`;
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success(t('settings.urlCopied'));
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="settings-page">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl font-bold text-slate-900">{t('settings.title')}</span>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSelector />
              <span className="text-sm text-slate-600">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                {t('common.dashboard')}
              </Button>
              <Button variant="outline" size="sm" onClick={logout}>
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        {/* WhatsApp Configuration */}
        <Card data-testid="card-whatsapp-settings">
          <CardHeader>
            <CardTitle>{t('settings.whatsappConfig')}</CardTitle>
            <CardDescription>{t('settings.whatsappConfigDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 mb-1">{t('settings.webhookUrl')}</p>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded mono">
                      {webhookUrl}
                    </code>
                  </div>
                  <Button variant="ghost" size="sm" onClick={copyWebhookUrl}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="phone_number_id">{t('settings.phoneNumberId')}</Label>
              <Input
                id="phone_number_id"
                value={whatsappConfig.phone_number_id}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number_id: e.target.value })}
                placeholder="123456789012345"
                data-testid="input-phone-number-id"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_token">{t('settings.accessToken')}</Label>
              {accessTokenConfigured && !editingAccessToken ? (
                <div className="flex flex-col gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3" data-testid="access-token-saved-state">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                    <div>
                      <p className="font-mono text-sm tracking-wider text-slate-800" aria-label={t('settings.accessTokenStored')}>
                        ••••••••••••••••••••
                      </p>
                      <p className="text-xs text-emerald-800">{t('settings.accessTokenStoredHint')}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingAccessToken(true)}
                    data-testid="button-replace-access-token"
                  >
                    {t('settings.replaceAccessToken')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      id="access_token"
                      type="password"
                      autoComplete="new-password"
                      value={whatsappConfig.access_token}
                      onChange={(e) => setWhatsappConfig({ ...whatsappConfig, access_token: e.target.value })}
                      placeholder="EAAxxxxx..."
                      className="font-mono"
                      data-testid="input-access-token"
                    />
                    {accessTokenConfigured && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setWhatsappConfig(prev => ({ ...prev, access_token: '' }));
                          setEditingAccessToken(false);
                        }}
                        data-testid="button-cancel-access-token-replacement"
                      >
                        {t('common.cancel')}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {accessTokenConfigured
                      ? t('settings.replaceAccessTokenHint')
                      : t('settings.accessTokenRequiredHint')}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="verify_token">{t('settings.verifyToken')}</Label>
              <Input
                id="verify_token"
                value={whatsappConfig.verify_token}
                readOnly
                data-testid="input-verify-token"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">{t('settings.phoneNumber')}</Label>
              <Input
                id="phone_number"
                value={whatsappConfig.phone_number}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number: e.target.value })}
                placeholder="+41791234567"
                data-testid="input-phone-number"
              />
            </div>

            <Button onClick={handleWhatsAppSave} disabled={loading} data-testid="button-save-whatsapp">
              {loading ? t('common.saving') : t('settings.saveWhatsApp')}
            </Button>
          </CardContent>
        </Card>

        {/* General Settings */}
        <Card data-testid="card-general-settings">
          <CardHeader>
            <CardTitle>{t('settings.generalSettings')}</CardTitle>
            <CardDescription>{t('settings.generalSettingsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="default_language">{t('settings.defaultLanguage')}</Label>
              <select
                id="default_language"
                value={settings.default_language}
                onChange={(e) => setSettings({ ...settings, default_language: e.target.value })}
                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm"
                data-testid="select-default-language"
              >
                <option value="it">{t('languages.it')}</option>
                <option value="de">{t('languages.de')}</option>
                <option value="en">{t('languages.en')}</option>
                <option value="es">{t('languages.es')}</option>
              </select>
            </div>

            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-slate-900">
                    {t('settings.forceLanguage')}
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    {t('settings.forceLanguageDesc')}
                  </p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, force_language_enabled: !settings.force_language_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.force_language_enabled ? 'bg-emerald-600' : 'bg-gray-300'
                  }`}
                  data-testid="toggle-force-language"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.force_language_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {settings.force_language_enabled && (
                <div className="space-y-2">
                  <Label htmlFor="force-language-value">{t('settings.forcedLanguage')}</Label>
                  <select
                    id="force-language-value"
                    value={settings.force_language_value || 'it'}
                    onChange={(e) => setSettings({ ...settings, force_language_value: e.target.value })}
                    className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm"
                    data-testid="select-force-language-value"
                  >
                    <option value="it">{t('languages.it')}</option>
                    <option value="de">{t('languages.de')}</option>
                    <option value="en">{t('languages.en')}</option>
                    <option value="es">{t('languages.es')}</option>
                  </select>
                </div>
              )}
            </div>

            {/* LLM Decision Layer removed from tenant UI - managed server-side only */}

            <div className="space-y-2">
              <Label htmlFor="durations">{t('settings.durations')}</Label>
              <Input
                id="durations"
                value={settings.durations}
                onChange={(e) => setSettings({ ...settings, durations: e.target.value })}
                data-testid="input-durations"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="external_details_link">{t('settings.externalDetailsLink')}</Label>
              <Input
                id="external_details_link"
                value={settings.external_details_link}
                onChange={(e) => setSettings({ ...settings, external_details_link: e.target.value })}
                placeholder="https://tuosito.com/dettagli"
                data-testid="input-external-details-link"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pricing_text">{t('settings.pricingText')}<SourceBadge source={fieldSources.pricing_text} /></Label>
              <Textarea
                id="pricing_text"
                value={settings.pricing_text}
                onChange={(e) => setSettings({ ...settings, pricing_text: e.target.value })}
                rows={2}
                data-testid="textarea-pricing-text"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rules_text">{t('settings.rules')}</Label>
              <Textarea
                id="rules_text"
                value={settings.rules_text}
                onChange={(e) => setSettings({ ...settings, rules_text: e.target.value })}
                rows={2}
                data-testid="textarea-rules-text"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location_text">{t('settings.locationInfo')}<SourceBadge source={fieldSources.location_text} /></Label>
              <Textarea
                id="location_text"
                value={settings.location_text}
                onChange={(e) => setSettings({ ...settings, location_text: e.target.value })}
                rows={2}
                data-testid="textarea-location-text"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="welcome_text">{t('settings.welcomeMessage')}</Label>
              <Textarea
                id="welcome_text"
                value={settings.welcome_text}
                onChange={(e) => setSettings({ ...settings, welcome_text: e.target.value })}
                rows={2}
                data-testid="textarea-welcome-text"
              />
            </div>

            <Button onClick={handleSettingsSave} disabled={loading} data-testid="button-save-settings">
              {loading ? t('common.saving') : t('settings.saveSettings')}
            </Button>
          </CardContent>
        </Card>

        {/* Studio Configuration */}
        <Card data-testid="card-studio-settings">
          <CardHeader>
            <CardTitle>{t('settings.studioConfig')}</CardTitle>
            <CardDescription>{t('settings.studioConfigDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="studio_public_name">{t('settings.studioName')}<SourceBadge source={fieldSources.studio_public_name} /></Label>
              <Input
                id="studio_public_name"
                value={settings.studio_public_name || ''}
                onChange={(e) => setSettings({ ...settings, studio_public_name: e.target.value })}
                placeholder="Il Mio Studio"
                data-testid="input-studio-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="public_info_url">{t('settings.publicInfoUrl')}<SourceBadge source={fieldSources.public_info_url} /></Label>
              <Input
                id="public_info_url"
                value={settings.public_info_url || ''}
                onChange={(e) => setSettings({ ...settings, public_info_url: e.target.value })}
                placeholder="https://tuosito.com/info"
                data-testid="input-public-info-url"
                className={
                  settings.public_info_url && 
                  !settings.public_info_url.startsWith('http://') && 
                  !settings.public_info_url.startsWith('https://') 
                    ? 'border-red-500' 
                    : ''
                }
              />
              {(!settings.public_info_url || 
                (!settings.public_info_url.startsWith('http://') && 
                 !settings.public_info_url.startsWith('https://'))) && (
                <Alert variant="warning" className="mt-2">
                  <AlertDescription className="text-xs">
                    <strong>Warning:</strong> {t('settings.publicInfoUrlWarning')}
                  </AlertDescription>
                </Alert>
              )}
              <p className="text-xs text-slate-500">
                {t('settings.publicInfoUrlHint')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="girls_list">{t('settings.girlsList')}<SourceBadge source={fieldSources.girls_list} /></Label>
              <Textarea
                id="girls_list"
                value={girlsText}
                onChange={(e) => setGirlsText(e.target.value)}
                rows={4}
                placeholder="Sofia\nFatima\nMaria"
                data-testid="textarea-girls-list"
              />
              <p className="text-xs text-slate-500">
                {t('settings.girlsListHint')}
              </p>
            </div>

            <Button onClick={handleSettingsSave} disabled={loading} data-testid="button-save-studio">
              {loading ? t('common.saving') : t('settings.saveStudio')}
            </Button>
          </CardContent>
        </Card>

        {/* Website Knowledge */}
        <WebsiteKnowledgeCard
          websiteUrl={websiteUrl}
          setWebsiteUrl={setWebsiteUrl}
          analyzing={analyzing}
          analyzeStatus={analyzeStatus}
          knowledgeData={knowledgeData}
          onAnalyze={handleAnalyzeWebsite}
        />
      </div>
    </div>
  );
}
